import { formatMonthDaysList, resolveMonthDaysFromRule, sortMonthDays } from './month-days.js';
const RECURRENCE_TYPES = ['once', 'daily', 'weekly', 'monthly', 'yearly', 'interval'];
const RECURRENCE_ENDS = ['never', 'on_date', 'after_count'];
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
export function toIsoDateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${String(year)}-${month}-${day}`;
}
/** Display ISO calendar date (`YYYY-MM-DD`) as `DD.MM.YYYY` in UI summaries. */
function formatIsoDateForDisplay(isoDate) {
    const match = ISO_DATE_PATTERN.exec(isoDate);
    if (!match) {
        return isoDate;
    }
    const year = match[1];
    const month = match[2];
    const day = match[3];
    if (!year || !month || !day) {
        return isoDate;
    }
    return `${day}.${month}.${year}`;
}
export function parseIsoDateString(value) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
}
/** Calendar validation for YYYY-MM-DD — independent of server timezone (no UTC round-trip). */
export function isValidLocalDateString(value) {
    if (!ISO_DATE_PATTERN.test(value)) {
        return false;
    }
    const [year, month, day] = value.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined) {
        return false;
    }
    const parsed = new Date(year, month - 1, day);
    return (parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day);
}
export function createDefaultRecurrenceRule(partial = {}) {
    const type = partial.type ?? 'weekly';
    const base = {
        type,
        startDate: partial.startDate ?? toIsoDateString(new Date()),
        time: partial.time ?? '09:00',
        timezone: partial.timezone ?? 'Europe/Moscow',
        ends: partial.ends ?? 'never',
        ...partial,
    };
    return normalizeRecurrenceRule(base);
}
export function normalizeRecurrenceRule(rule) {
    const type = rule.type;
    const normalized = {
        type,
        startDate: rule.startDate,
        time: rule.time ?? '09:00',
        timezone: rule.timezone,
        ends: rule.ends,
    };
    if (type === 'once' || type === 'interval') {
        normalized.startDate = rule.startDate ?? toIsoDateString(new Date());
    }
    if (type === 'weekly') {
        const weekdays = rule.weekdays !== undefined ? [...rule.weekdays] : [1, 2, 3, 4, 5];
        normalized.weekdays = [...weekdays].sort((a, b) => {
            const orderA = a === 0 ? 7 : a;
            const orderB = b === 0 ? 7 : b;
            return orderA - orderB;
        });
    }
    if (type === 'monthly') {
        const monthDays = rule.monthDays !== undefined
            ? sortMonthDays(rule.monthDays)
            : rule.monthDay !== undefined && rule.monthDay !== null
                ? [rule.monthDay]
                : [1];
        normalized.monthDays = monthDays;
        normalized.monthDay = monthDays[0] ?? 1;
    }
    if (type === 'yearly') {
        normalized.yearDate = {
            month: rule.yearDate?.month ?? 1,
            day: rule.yearDate?.day ?? 1,
        };
    }
    if (type === 'interval') {
        normalized.interval = rule.interval ?? 7;
        normalized.intervalUnit = rule.intervalUnit ?? 'day';
        normalized.startDate = rule.startDate ?? toIsoDateString(new Date());
        if (normalized.intervalUnit === 'month') {
            const monthDays = rule.monthDays !== undefined
                ? sortMonthDays(rule.monthDays)
                : rule.monthDay !== undefined && rule.monthDay !== null
                    ? [rule.monthDay]
                    : [1];
            normalized.monthDays = monthDays;
            normalized.monthDay = monthDays[0] ?? 1;
        }
    }
    if (normalized.ends === 'on_date') {
        normalized.endDate = rule.endDate ?? normalized.startDate;
    }
    if (normalized.ends === 'after_count') {
        normalized.occurrences = rule.occurrences ?? 10;
    }
    return normalized;
}
export { resolveMonthDaysFromRule } from './month-days.js';
export function formatRecurrenceRule(rule, t) {
    const normalized = normalizeRecurrenceRule(rule);
    const time = normalized.time ?? '09:00';
    switch (normalized.type) {
        case 'once':
            return t('recurrence.summary.once', {
                date: formatIsoDateForDisplay(normalized.startDate ?? ''),
                time,
            });
        case 'daily':
            return t('recurrence.summary.daily', { time });
        case 'weekly': {
            const days = (normalized.weekdays ?? [])
                .map((day) => t(`weekdays.long.${String(day)}`))
                .join(', ');
            return t('recurrence.summary.weekly', { days, time });
        }
        case 'monthly':
            return t('recurrence.summary.monthly', {
                days: formatMonthDaysList(resolveMonthDaysFromRule(normalized)),
                time,
            });
        case 'yearly': {
            const month = normalized.yearDate?.month ?? 1;
            const day = normalized.yearDate?.day ?? 1;
            return t('recurrence.summary.yearly', {
                month: t(`months.${String(month)}`),
                day,
                time,
            });
        }
        case 'interval': {
            const interval = normalized.interval ?? 1;
            const unit = t(`recurrence.intervalUnit.${normalized.intervalUnit ?? 'day'}`);
            const start = formatIsoDateForDisplay(normalized.startDate ?? '');
            return t('recurrence.summary.interval', { interval, unit, start, time });
        }
        default:
            return time;
    }
}
//# sourceMappingURL=recurrence-rule.js.map